import { useEffect } from 'react';

export interface HotkeyBinding {
  combo: string; // e.g. 'alt+q', 'arrowright', 'ctrl+space'
  handler: (e: KeyboardEvent) => void;
}

// `extraDocument` lets the caller also listen on a second document — the detached
// viewer's pop-out window — so hotkeys fire whichever window has focus. A single
// keypress only dispatches in the focused window's document, so binding both can't
// double-fire.
export function useDocumentHotkeys(
  bindings: HotkeyBinding[],
  enabled = true,
  extraDocument?: Document | null
) {
  useEffect(() => {
    if (!enabled) return;

    function onKeydown(e: KeyboardEvent) {
      // Ignore hotkeys while typing in a field (matches angular-hotkeys, which skips
      // inputs by default) so alt+r / ? / left etc. don't fire mid-edit.
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' || target.isContentEditable)) {
        return;
      }

      const parts: string[] = [];
      if (e.ctrlKey) parts.push('ctrl');
      if (e.altKey) parts.push('alt');
      if (e.shiftKey) parts.push('shift');
      // Normalize key names so registrations read naturally: ' ' -> 'space',
      // 'ArrowLeft' -> 'left', etc.
      let key = e.key.toLowerCase();
      if (key === ' ') key = 'space';
      else if (key.startsWith('arrow')) key = key.slice(5);
      parts.push(key);
      const combo = parts.join('+');

      for (const { combo: target, handler } of bindings) {
        if (combo === target.toLowerCase()) {
          // Stop the browser default AND any other page listeners (e.g. a
          // screenshot extension's content script that also binds alt+p) from
          // seeing this event. stopImmediatePropagation covers listeners on
          // this same node; the capture-phase registration below lets us win
          // the race against extensions listening in the bubbling phase.
          e.preventDefault();
          e.stopImmediatePropagation();
          handler(e);
          return;
        }
      }
    }

    // Capture phase: we see the keydown before bubbling-phase listeners, so we
    // can suppress it before a page-injected extension handler fires.
    const targets: Document[] = [document];
    if (extraDocument && extraDocument !== document) targets.push(extraDocument);
    targets.forEach((d) => d.addEventListener('keydown', onKeydown, true));
    return () => targets.forEach((d) => {
      // The detached window may already be closed by the time we detach the
      // listener (pop-in closes it before the detached flag flips), so its
      // document can be gone — ignore the resulting error.
      try { d.removeEventListener('keydown', onKeydown, true); } catch { /* noop */ }
    });
  }, [bindings, enabled, extraDocument]);
}
